import { useCallback, useEffect, useRef, useState } from "react";
import { logger } from "src/logger";

export function useControlled<
  StateValue,
  ControlledState extends StateValue = StateValue,
>({
  controlled,
  default: defaultProp,
  name,
  state = "value",
  onChange,
}: {
  name: string;
  state: string;
  controlled?: ControlledState;
  default: StateValue;
  onChange?: (value: StateValue) => void;
}): [
  StateValue,
  (value: StateValue | { (prev: StateValue): StateValue }) => void,
] {
  // isControlled is ignored in the hook dependency lists as it should never change.
  const { current: isControlled } = useRef(controlled !== undefined);
  const [valueState, setValue] = useState(defaultProp);
  const value = isControlled ? controlled : valueState;

  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      if (isControlled !== (controlled !== undefined)) {
        logger.error(
          [
            `A component is changing the ${
              isControlled ? "" : "un"
            }controlled ${state} state of ${name} to be ${
              isControlled ? "un" : ""
            }controlled.`,
            "Elements should not switch from uncontrolled to controlled (or vice versa).",
            `Decide between using a controlled or uncontrolled ${name} ` +
              "element for the lifetime of the component.",
            "The nature of the state is determined during the first render. It's considered controlled if the value is not `undefined`.",
            "More info: https://fb.me/react-controlled-components",
          ].join("\n"),
        );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state, name, controlled]);

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { current: defaultValue } = useRef(defaultProp);

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      if (!isControlled && defaultValue !== defaultProp) {
        logger.error(
          [
            `A component is changing the default ${state} state of an uncontrolled ${name} after being initialized. ` +
              `To suppress this warning opt to use a controlled ${name}.`,
          ].join("\n"),
        );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(defaultProp)]);
  }

  const setValueIfUncontrolled = useCallback(
    (newValue: StateValue | { (prev: StateValue): StateValue }) => {
      if (!isControlled) {
        if (typeof newValue === "function") {
          setValue((prev) => {
            // @ts-ignore
            const nextValue = newValue(prev);
            onChange?.(nextValue);
            return nextValue;
          });
        } else {
          setValue(newValue);
          onChange?.(newValue);
        }
      } else {
        if (typeof newValue === "function") {
          if (process.env.NODE_ENV !== "production") {
            logger.error(
              `functional update of ${state} state of ${name} is only supported in uncontrolled mode`,
            );
          }
        } else {
          onChange?.(newValue);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onChange],
  );

  // todo: don't use type-casting
  return [value as StateValue, setValueIfUncontrolled];
}
